import React, { useEffect, useState } from 'react';
import BackBtn from '../../../utils/BackBtn';
import { CgCloseR } from "react-icons/cg";
import Loading from '../../../component/Loading/Loading';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useAxiosInstance from '../../../services/useAxiosInstance';
import { Check, X, Upload, FileText, File, Image as ImageIcon } from 'lucide-react';
import { FaFilePdf } from "react-icons/fa";
import GlassmorphicModal from "../../../component/ReusableComponents/GlassmorphicModal";
import AlertMsg from '../../../component/AlertMsg/AlertMsg';

const ViolationDetails = () => {
    const axiosInstance = useAxiosInstance();
    const { ViolationId } = useParams();
    const [violationData, setViolationData] = useState(null);
    const [violationDocumentData, setViolationDocumentData] = useState([]);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const communityId = useSelector((state) => state.auth.communityId);
    const userId = useSelector((state) => state.auth.userId);
    const roleId = useSelector((state) => state.auth.roleId);
    const [loading, setLoading] = useState(false);
    const communityIds = useSelector((state) => state.auth.communityId);
    const communityIdget = communityIds;
    const [alert, setAlert] = useState({ visible: false, message: '', type: '' });

    const [formData, setFormData] = useState({
        activeStatus: true,
        communityId: communityId,
        createdByDate: new Date().toISOString(),
        createdById: userId,
        description: "",
        docUrl: "",
        violationDocumentId: 0,
        violationId: parseInt(ViolationId)
    });

    // Helper function to detect file type and get appropriate display
    const getFileDisplay = (fileUrl) => {
        if (!fileUrl) return null;

        // Extract filename from URL
        const urlParts = fileUrl.split('/');
        const fileName = urlParts[urlParts.length - 1];
        const fileExtension = fileName.split('.').pop()?.toLowerCase();

        // Define file type categories
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
        const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'];

        const isImage = imageExtensions.includes(fileExtension);
        const isVideo = videoExtensions.includes(fileExtension);

        if (isVideo) {
            return {
                type: 'video',
                element: (
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="block">
                        <video
                            controls
                            src={fileUrl}
                            className="w-20 h-20 object-cover rounded cursor-pointer"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                            }}
                        />
                        <div className="hidden w-20 h-20 border rounded flex items-center justify-center bg-gray-100">
                            <File size={24} className="text-gray-500" />
                            <span className="text-xs text-gray-500 ml-1">VIDEO</span>
                        </div>
                    </a>
                )
            };
        } else if (isImage) {
            return {
                type: 'image',
                element: (
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="block">
                        <img
                            src={fileUrl}
                            alt="Document thumbnail"
                            className="w-20 h-20 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                            }}
                        />
                        <div className="hidden w-20 h-20 border rounded flex items-center justify-center bg-gray-100">
                            <ImageIcon size={24} className="text-gray-500" />
                            <span className="text-xs text-gray-500 ml-1">IMG</span>
                        </div>
                    </a>
                )
            };
        } else {
            // For non-image, non-video files, show appropriate icon and filename
            let icon;
            let label;

            switch (fileExtension) {
                case 'pdf':
                    icon = <FaFilePdf size={24} className="text-red-500" />;
                    label = 'PDF';
                    break;
                case 'doc':
                case 'docx':
                    icon = <FileText size={24} className="text-blue-500" />;
                    label = 'DOC';
                    break;
                case 'txt':
                    icon = <FileText size={24} className="text-gray-500" />;
                    label = 'TXT';
                    break;
                default:
                    icon = <File size={24} className="text-gray-500" />;
                    label = fileExtension?.toUpperCase() || 'FILE';
            }

            return {
                type: 'document',
                element: (
                    <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-20 h-20 border rounded flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                        {icon}
                        <span className="text-xs text-gray-600 mt-1 text-center leading-tight">
                            {label}
                        </span>
                    </a>
                )
            };
        }
    };

    const fetchViolationData = async () => {
        try {
            const response = await axiosInstance.get(`/api/violation/getViolationByViolationId/${ViolationId}`);

            const jsonData = response.data;
            if (Array.isArray(jsonData) && jsonData.length > 0) {
                setViolationData(jsonData[0]);
            } else {
                console.warn("Unexpected data format for violation data:", jsonData);
                setViolationData(null);
            }
        } catch (error) {
            console.error("Error fetching violation data:", error);
        }
    };

    const fetchViolationDocumentData = async () => {
        try {
            const response = await axiosInstance.get(`/api/violationDocument/violation/${ViolationId}`);

            setViolationDocumentData(response.data);
        } catch (error) {
            console.error("Error fetching violation document data:", error);
        }
    };
    useEffect(() => {
        fetchViolationData();
        fetchViolationDocumentData();
    }, [ViolationId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value.trim()
        }));
    };

    const [selectedFile, setSelectedFile] = useState(null);

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setShowDocumentModal(false);
        setLoading(true);

        let newDocUrl = formData.docUrl;

        if (selectedFile) {
            const uploadFormData = new FormData();
            uploadFormData.append('file', selectedFile);

            try {
                const uploadResponse = await axiosInstance.post(
                    `/api/violationDocument/violationUpload`,
                    uploadFormData);
                newDocUrl = uploadResponse.data;
                console.log(newDocUrl);

            } catch (error) {
                console.error('Error uploading file', error);
                setAlert({ visible: true, message: 'Error uploading file. Please try again.', type: 'error' });
                setLoading(false);
                return;
            }
        }

        try {
            const response = await axiosInstance.post(`/api/violationDocument/add`, {
                ...formData,
                docUrl: newDocUrl,
            });
            console.log(response.data);
            fetchViolationData();
            fetchViolationDocumentData();
            setLoading(false);
            setAlert({ visible: true, message: 'Document uploaded successfully!', type: 'success' });
            setFormData({
                ...formData,
                description: "",
                docUrl: "",
                violationDocumentId: 0,
                violationId: parseInt(ViolationId)
            });
        } catch (error) {
            console.error("Error submitting form:", error);
            setAlert({ visible: true, message: 'Error submitting form. Please try again.', type: 'error' });
            setLoading(false);
        }
    };
    const [paymentFormData, setPaymentFormData] = useState({
        amenityBookingId: null,
        amount: 0,
        clientId: 0,
        communityId: 0,
        createdById: userId,
        createdDate: new Date().toISOString(),
        isApproved: true,
        modifiedById: null,
        modifiedDate: new Date().toISOString(),
        monthlyRevenueId: null,
        paymentBank: "",
        paymentDate: "",
        paymentDetailId: null,
        paymentMethod: "",
        paymentStatus: "",
        paymentType: "",
        reason: "Violation",
        serviceRequestId: null,
        violationId: 0
    });

    const updateViolationStatus = async (statusId) => {
        console.log(statusId);
        setLoading(true);

        try {
            const response = await axiosInstance.patch(`/api/violation/updateViolationStatus/${ViolationId}`, null, {
                params: { violationStatusId: statusId }
            });
            if (response.status === 200) {
                console.log("start123");

                if (response.data.violationStatusId === 3) {
                    console.log('start-pay1');

                    const updatedPaymentFormData = {
                        ...paymentFormData,
                        violationId: response.data.violationId,
                        amount: response.data.amount,
                        clientId: response.data.clientId,
                        communityId: response.data.communityId,
                    };

                    console.log('ok', updatedPaymentFormData);

                    const paymentResponse = await axiosInstance.post(
                        `/api/public/paymentdetail/add`,
                        updatedPaymentFormData,
                        {
                            headers: {
                                "Content-Type": "application/json",
                                "X-User-Id": userId,
                                "X-Community-Id": communityId,
                            },
                        }
                    );

                    if (paymentResponse.status === 201) {
                        console.log("okkkkkkkkkkkkkkkkk");
                        setAlert({ visible: true, message: 'Violation accepted successfully!', type: 'success' });
                        console.log(updatedPaymentFormData);
                    } else {
                        setAlert({ visible: true, message: 'Failed to process payment details.', type: 'error' });
                        console.log(updatedPaymentFormData);
                        console.log("problem");
                    }
                } else if (statusId === 4) {
                    setAlert({ visible: true, message: 'Violation rejected. Please upload documentation.', type: 'info' });
                } else if (statusId === 5) {
                    setAlert({ visible: true, message: 'Violation reassigned successfully.', type: 'success' });
                } else if (statusId === 6) {
                    setAlert({ visible: true, message: 'Violation rejected successfully.', type: 'success' });
                }

                fetchViolationData();
                fetchViolationDocumentData();
                setLoading(false);
            }
        } catch (error) {
            console.error("Error updating violation status:", error);
            setAlert({ visible: true, message: 'Error updating violation status.', type: 'error' });
            setLoading(false);
        }
    };

    const openApprovalModal = () => {
        setShowApprovalModal(true);
    };

    const closeModal = () => {
        setShowApprovalModal(false);
    };

    const openDocumentModal = () => {
        setShowDocumentModal(true);
        updateViolationStatus(4);
    };

    const closeDModal = () => {
        setShowDocumentModal(false);
    };

    // Modal footers
    const approvalModalFooter = (
        <div className="flex justify-end space-x-2">
            <button
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={closeModal}
            >
                Cancel
            </button>
            <button
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                onClick={() => {
                    updateViolationStatus(3);
                    closeModal();
                }}
            >
                Accept
            </button>
            <button
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                onClick={() => {
                    openDocumentModal();
                    closeModal();
                }}
            >
                Reject
            </button>
        </div>
    );

    const documentModalFooter = (
        <div className="flex justify-end space-x-2">
            <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={closeDModal}
            >
                Cancel
            </button>
            <button
                type="button"
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                onClick={handleSubmit}
            >
                Submit
            </button>
        </div>
    );

    if (loading || !violationData) {
        return <Loading />;
    }

    return (
        <div className="bg-gray-50 min-h-screen md:p-4">
            <div className="max-w-7xl mx-auto">
                {alert.visible && (
                    <AlertMsg
                        message={alert.message}
                        type={alert.type}
                        onClose={() => setAlert({ ...alert, visible: false })}
                    />
                )}

                <div className="mb-4">
                    <BackBtn />
                </div>

                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">Violation Details</h1>
                    <div className="flex flex-wrap gap-2">
                        {(violationData.violationStatusId === 2 || violationData.violationStatusId === 5) && (
                            <>
                                {(roleId === 1003) && (
                                    <>
                                        <button
                                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
                                            onClick={() => updateViolationStatus(3)}
                                        >
                                            <Check size={18} />
                                            <span>Accept</span>
                                        </button>
                                        <button
                                            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 flex items-center gap-2"
                                            onClick={openDocumentModal}
                                        >
                                            <X size={18} />
                                            <span>Reject</span>
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                        {violationData.violationStatusId === 4 && (
                            <>
                                {(roleId === 1001 || roleId === 1002) && (
                                    <>
                                        <button
                                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
                                            onClick={() => updateViolationStatus(5)}
                                        >
                                            <Check size={18} />
                                            <span>Reassign</span>
                                        </button>
                                        <button
                                            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 flex items-center gap-2"
                                            onClick={() => updateViolationStatus(6)}
                                        >
                                            <X size={18} />
                                            <span>Reject</span>
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Violation Type</label>
                                <input
                                    type="text"
                                    value={violationData.violationTypeName}
                                    readOnly
                                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Violation Status</label>
                                <input
                                    type="text"
                                    value={violationData.violationStatusName}
                                    readOnly
                                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Violation Date</label>
                                <input
                                    type="text"
                                    value={new Date(violationData.violationDate).toLocaleDateString()}
                                    readOnly
                                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                <input
                                    type="text"
                                    value={violationData.amount}
                                    readOnly
                                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                                <input
                                    type="text"
                                    value={violationData.remarks}
                                    readOnly
                                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-200 mt-4">
                        <div className="px-6 py-4">
                            <h2 className="text-lg font-semibold text-gray-800">Documents</h2>
                        </div>
                        <div className=" pb-6">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-blue-400">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Description</th>
                                            <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Created By</th>
                                            <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Created Date</th>
                                            <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Document</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {violationDocumentData.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">No documents found</td>
                                            </tr>
                                        ) : (
                                            violationDocumentData.map((doc, index) => (
                                                <tr key={index} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{doc.description}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{doc.createdByName}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {new Date(doc.createdByDate).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {doc.docUrl ? (
                                                            getFileDisplay(doc.docUrl)?.element
                                                        ) : (
                                                            <div className="w-20 h-20 border rounded flex items-center justify-center bg-gray-100">
                                                                <File size={24} className="text-gray-400" />
                                                                <span className="text-xs text-gray-500 ml-1">No File</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Approval Modal using GlassmorphicModal */}
            <GlassmorphicModal
                isOpen={showApprovalModal}
                onClose={closeModal}
                title="Violation Approval"
                size="sm"
                footer={approvalModalFooter}
            >
                <p className="text-gray-700">
                    Do you accept the violation?
                </p>
            </GlassmorphicModal>

            {/* Document Upload Modal using GlassmorphicModal */}
            <GlassmorphicModal
                isOpen={showDocumentModal}
                onClose={closeDModal}
                title="Document Upload"
                size="md"
                footer={documentModalFooter}
            >
                <form className="grid grid-cols-1 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            required
                            className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Document <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type="file"
                                name="docUrl"
                                onChange={handleFileChange}
                                className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <FileText size={18} className="text-gray-400" />
                            </div>
                        </div>
                        {selectedFile && (
                            <p className="text-xs text-gray-500 mt-1">
                                Selected file: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                            </p>
                        )}
                    </div>
                    <div className="text-sm text-red-500">* indicates required field</div>
                </form>
            </GlassmorphicModal>
        </div>
    );
};

export default ViolationDetails;